import { MouseEvent, useEffect, useMemo, useState } from 'react'
import Auth from '@aws-amplify/auth'

import { convertToPrice } from 'featureSliced/shared/utils/convertToPrice'

import { Header } from 'components/Header'
import { RenderIf } from 'components/RenderIf'
import { errorHandler } from 'components/ErrorHandler'

import { FooterPaymentQRM } from './Footer'
import { useWebLayout } from 'featureSliced/shared/hooks/useWebLayout'
import { CheckoutWeb } from 'featureSliced/widgets/WebLayout/CheckoutWeb'

import { clickCollectGET } from 'featureSliced/shared/api/clickCollect/GET'
import { Spinner } from 'featureSliced/entities/Spinner'

import { Cart } from 'featureSliced/widgets/Cart'
import { Tips } from 'featureSliced/widgets/Tips'
import { SubFooter } from 'featureSliced/widgets/Cart/SubFooter'
import { AgeProtectionModal } from 'featureSliced/widgets/AgeProtectionModal'

import { useProductProtection } from 'featureSliced/shared/hooks/useProductProtection'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useOpenModal } from 'featureSliced/shared/hooks/useOpenModal'
import { usePayment } from 'featureSliced/shared/hooks/usePayment'
import { CurrentPageType } from './types'

import { EmptyBlock, PaymentContainer } from './styled'
import { IProduct } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { IBucketProduct } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'
import { ContextMenu } from 'components/ContextMenu'

import basket from 'assets/basket_small.svg'
import trashCan from 'assets/trashCan.svg'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { QRMBucketSlice } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer'
import { useOpenProduct } from 'featureSliced/shared/hooks/openModals/useOpenProductModal'
import { getProductDiscount } from 'featureSliced/shared/utils/getProductDiscount'
import { findTemplateUnavailableLines } from 'featureSliced/shared/utils/templateDays'
import { SelfService } from 'featureSliced/widgets/SelfService'
import { getNavLink } from 'featureSliced/shared/utils/getNavLink'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ToastsSlice } from 'featureSliced/shared/store/reducers/ToasterReducer'
import { CardsModal } from 'featureSliced/padges/PaymentQRM/QRMCardsModal'
import { useGetProductPrice } from 'featureSliced/shared/hooks/useGetProductPrice/indexT'
import { ConfirmationModal } from 'featureSliced/features/ConfirmationModal'
import { getSoldOutItems } from 'featureSliced/shared/store/reducers/CategoriesReducer/actionCreator'

export type IPosition = {
  x: number
  y: number
}

export const CheckoutQRM = () => {
  // Hooks
  const navigate = useNavigate()
  const t = useTranslations()
  const openModal = useOpenModal()
  const dispatch = useMyDispatch()
  const [searchParams] = useSearchParams()
  const openQRMProductModal = useOpenProduct()
  const { getAllProductsPrice, applyOfferPricesToBucket } = useGetProductPrice()
  const { startQRMAfterMealPayment: startPayment } = usePayment()
  const { isProductHighlyProtected, isProductProtected } =
    useProductProtection()

  // Store
  const discounts = useMySelector((state) => state.specials?.discounts)
  const offerPrices = useMySelector((state) => state.specials?.offerPrices)
  const isSpecialsLoaded = useMySelector(
    (state) => state.specials?.isSpecialsLoaded
  )
  const productsList = useMySelector((state) =>
    state.categories.items.flatMap((cat) => cat.products)
  )
  const isUserAuth = useMySelector((state) => state.auth.isLoggedIn)
  const currency = useMySelector((state) => state.app.companyData.Currency)
  const selectedQRMProducts = useMySelector((state) => state.bucket.products)
  const isProductsLoaded = useMySelector((state) => state.categories.isLoaded)
  const isSelfServiceEnabled = useMySelector(
    (state) => state.app.menu.selfService
  )
  const isTipsAllowed = useMySelector((state) => state.app.menu.tips)
  const { isWeb } = useWebLayout()
  const openToast = ToastsSlice.actions.openToast
  const removeProductFromBucket = QRMBucketSlice.actions.removeProductFromBucket

  // useMemo
  const bucketProducts = useMemo(() => {
    return applyOfferPricesToBucket(selectedQRMProducts)?.map((el) => ({
      ...el,
      discountP: el.offerApplied ? 0 : getProductDiscount(el, discounts),
    }))
  }, [selectedQRMProducts, isProductsLoaded, offerPrices])

  // State
  const [position, setPosition] = useState<IPosition | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<IProduct>()
  const [selectedBProduct, setSelectedBProduct] = useState<IBucketProduct>()
  // QRM-641: on the web layout the cart lives in the column on the menu page and
  // the order recap sits beside the form, so the cart STEP has nothing left to
  // show. Skipping it mirrors exactly what the footer's cart branch does
  // (handleNextCartAndSplit) — and when neither self-service nor tips is switched
  // on, 'cart' IS the pay step, so it has to stay.
  const [currentPage, setCurrentPage] = useState<CurrentPageType>(() => {
    if (!isWeb) return 'cart'
    if (isSelfServiceEnabled) return 'selfService'
    if (isTipsAllowed) return 'tips'
    return 'cart'
  })
  const [isAVGConfirmed, setIsAVGConfirmed] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [tipsValue, setTipsValue] = useState<number>(0)
  const [isSSButtonEnabled, setIsSSButtonEnabled] = useState<boolean>(false)
  const [loyaltyCardBalance, setLoyaltyCardBalance] = useState<number>(0)

  // useEffect
  useEffect(() => {
    if (!bucketProducts?.length) {
      dispatch(
        openToast({
          message: t('errors.basketIsEmpty'),
          type: 'warning',
        })
      )
      navigate(getNavLink(searchParams, 'menu'))
    }
  }, [bucketProducts])

  useEffect(() => {
    checkIfLoyaltyCardExists()
  }, [])

  // Functions
  const getResultPriceWithCurrency = () => {
    const total = getAllProductsPrice(bucketProducts)
    return `${convertToPrice(total + tipsValue)} ${currency || ''}`
  }

  const getIsNextAndPayDisabled = () => {
    if (
      !isProductsLoaded ||
      !isSpecialsLoaded ||
      !getAllProductsPrice(bucketProducts)
    )
      return true
    if (isSelfServiceEnabled && currentPage === 'selfService') {
      return (!isUserAuth && !isAVGConfirmed) || !isSSButtonEnabled
    } else {
      return !isUserAuth && !isAVGConfirmed
    }
  }

  const checkIfLoyaltyCardExists = async () => {
    try {
      if (isUserAuth) {
        const res = await clickCollectGET.customerCard()
        const total = Number(res?.total)
        setLoyaltyCardBalance(!isNaN(total) ? total : 0)
      }
    } catch (e) {
      errorHandler(e)
    }
  }

  const openAgeProtectionModal = (isHighlyProtected = false) => {
    openModal({
      id: 'AGE_PROTECTION_MODAL',
      title: t('common.confirmYourAge'),
      components: [
        ({ closeThisModal }) => (
          <AgeProtectionModal
            isHighlyProtected={isHighlyProtected}
            startPayment={() => {
              startPaymentRequest()
              closeThisModal()
            }}
          />
        ),
      ],
    })
  }

  const startPaymentProccess = async () => {
    const templateUnavailableLines = findTemplateUnavailableLines(
      bucketProducts,
      productsList
    )
    if (templateUnavailableLines.length) {
      templateUnavailableLines.forEach((product) => {
        dispatch(removeProductFromBucket(product.uniqueId))
      })
      dispatch(
        openToast({
          message: t('products.someProductsNotAvailable') || '',
          type: 'warning',
        })
      )
      navigate(getNavLink(searchParams, 'menu'))
      return
    }

    const soldOutIds = await dispatch(getSoldOutItems())
    const soldOutProducts = bucketProducts?.filter((product) =>
      soldOutIds?.includes(product.productId)
    )

    if (soldOutProducts?.length) {
      // Remove all sold out products from bucket
      soldOutProducts.forEach((product) => {
        if (product.productId) {
          dispatch(removeProductFromBucket(product.uniqueId))
        }
      })

      dispatch(
        openToast({
          message: t('products.someProductsAreSoldOut') || '',
          type: 'warning',
        })
      )

      navigate(getNavLink(searchParams, 'menu'))
    } else if (
      bucketProducts?.some((product) =>
        isProductHighlyProtected(product.productId)
      )
    ) {
      openAgeProtectionModal(true)
    } else if (
      bucketProducts?.some((product) => isProductProtected(product.productId))
    ) {
      openAgeProtectionModal()
    } else {
      startPaymentRequest()
    }
  }

  const startPaymentRequest = async () => {
    if (isAVGConfirmed || isUserAuth) {
      // Prevent creating payment without table id
      const tableId = searchParams.get('table')
      const tableIdNumber = tableId ? Number(tableId) : 0

      if (!isSelfServiceEnabled && (!tableId || tableIdNumber === 0)) {
        openModal({
          id: 'TEMP_ERROR_MODAL',
          title: '',
          components: [
            ({ closeThisModal }) => (
              <ConfirmationModal
                text={t('errors.tableIdIsMissing')}
                onConfirm={() => {
                  closeThisModal()
                  navigate('/')
                }}
                okText={t('buttons.ok')}
              />
            ),
          ],
        })
      } else {
        try {
          setIsLoading(true)

          if (!isUserAuth) {
            await startPayment(
              bucketProducts,
              getAllProductsPrice(bucketProducts),
              tipsValue
            )
          }

          if (isUserAuth) {
            const currentUserInfo = await Auth.currentUserInfo()
            const attributes = currentUserInfo?.attributes
            if (
              (!attributes || !attributes?.['custom:active_card_token']) &&
              !loyaltyCardBalance
            ) {
              await startPayment(
                bucketProducts,
                getAllProductsPrice(bucketProducts),
                tipsValue
              )
            } else {
              setIsLoading(false)
              openModal({
                id: 'PAYMENT_CARDS_MODAL',
                // TODO: translations
                title: t('order.choosePaymentVariant'),
                components: [
                  ({ closeThisModal }) => (
                    <CardsModal
                      isLoyaltyCardExisting={!!loyaltyCardBalance}
                      isLoyaltyCardDisabled={
                        loyaltyCardBalance <
                        getAllProductsPrice(bucketProducts) + tipsValue
                      }
                      itemsToPay={bucketProducts}
                      tipsValue={tipsValue}
                      userCardData={attributes?.['custom:active_card_token']}
                      closeModal={closeThisModal}
                      total={getAllProductsPrice(bucketProducts)}
                    />
                  ),
                ],
              })
            }
          }
        } catch (e) {
          errorHandler(e)
        } finally {
          setIsLoading(false)
        }
      }
    }
  }

  const handleProductClick = (
    productId: number,
    e: MouseEvent<HTMLElement>,
    bucketProduct: any
  ) => {
    const findById = (items: IProduct[], id: number): IProduct | undefined => {
      for (const item of items) {
        if (item.id === id) return item
        if (item.innerCategoryItems?.length) {
          const found = findById(item.innerCategoryItems as IProduct[], id)
          if (found) return found
        }
      }
      return undefined
    }
    const product = findById(productsList || [], productId)
    if (product) {
      setSelectedBProduct(bucketProduct as IBucketProduct)
      setSelectedProduct(product)
      setPosition({
        x: e.pageX + 145 > window.innerWidth ? e.pageX - 125 : e.pageX,
        y: e.pageY,
      })
    }
  }
  ///

  const steps = (
    <>
      {/* SELF_SERVICE */}
      <RenderIf condition={currentPage === 'selfService'}>
        <SelfService setIsSSButtonEnabled={setIsSSButtonEnabled} />
      </RenderIf>

      {/* TIPS */}
      <RenderIf condition={currentPage === 'tips'}>
        <Tips
          total={getAllProductsPrice(bucketProducts)}
          tipsValue={tipsValue}
          onChange={setTipsValue}
        />
      </RenderIf>

      {/* SUBFOOTER */}
      <SubFooter
        isAVGConfirmed={isAVGConfirmed}
        setIsAVGConfirmed={setIsAVGConfirmed}
        isUserAuth={isUserAuth}
      />

      <FooterPaymentQRM
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        tipsValue={tipsValue}
        isLoading={isLoading}
        startPaymentProccess={startPaymentProccess}
        getResultPrice={getResultPriceWithCurrency}
        getIsNextAndPayDisabled={getIsNextAndPayDisabled}
      />
    </>
  )

  if (isWeb) {
    return <CheckoutWeb title={t('buttons.checkout')}>{steps}</CheckoutWeb>
  }

  return (
    <PaymentContainer>
      <Header />
      <EmptyBlock />
      <RenderIf condition={!isProductsLoaded}>
        <Spinner type="transparent" />
      </RenderIf>

      {/* CART */}
      <RenderIf condition={currentPage === 'cart'}>
        <Cart
          products={bucketProducts}
          handleProductClick={handleProductClick}
        />
      </RenderIf>

      {/* SELF_SERVICE */}
      <RenderIf condition={currentPage === 'selfService'}>
        <SelfService setIsSSButtonEnabled={setIsSSButtonEnabled} />
      </RenderIf>

      {/* TIPS */}
      <RenderIf condition={currentPage === 'tips'}>
        <Tips
          total={getAllProductsPrice(bucketProducts)}
          tipsValue={tipsValue}
          onChange={setTipsValue}
        />
      </RenderIf>

      {/* SUBFOOTER */}
      <RenderIf
        condition={currentPage === 'cart' || (!isAVGConfirmed && !isUserAuth)}
      >
        <SubFooter
          isAVGConfirmed={isAVGConfirmed}
          setIsAVGConfirmed={setIsAVGConfirmed}
          //
          isUserAuth={isUserAuth}
          // setIsUserAuth={setIsUserAuth}
        />
      </RenderIf>

      {/* CONTEXT */}
      <RenderIf condition={!!position}>
        <ContextMenu
          position={position as IPosition}
          discardPosition={() => setPosition(null)}
          items={contextMenuItems(
            t,
            selectedProduct as IProduct,
            selectedBProduct as IBucketProduct,
            (product: IProduct) =>
              openQRMProductModal(product, product.categoryId),
            (uniqueId: string) => dispatch(removeProductFromBucket(uniqueId)),
            !selectedBProduct?.menuItems?.length
          )}
        />
      </RenderIf>
      {/* FOOTER */}
      {/* <RenderIf condition={isFooterShown}> */}
      <FooterPaymentQRM
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        //
        tipsValue={tipsValue}
        isLoading={isLoading}
        //
        startPaymentProccess={startPaymentProccess}
        getResultPrice={getResultPriceWithCurrency}
        getIsNextAndPayDisabled={getIsNextAndPayDisabled}
      />
      {/* </RenderIf> */}
    </PaymentContainer>
  )
}

const contextMenuItems = (
  t: any,
  selectedProduct: IProduct,
  selectedBProduct: IBucketProduct,
  handleEditClick: (product: IProduct) => void,
  handleRemoveProduct: (id: string) => void,
  isEditEnabled: boolean
) => [
  ...(isEditEnabled
    ? [
        {
          component: (
            <>
              <img src={basket} alt="Edit" />
              {t('buttons.edit')}
            </>
          ),
          onClick: () => handleEditClick(selectedProduct),
        },
      ]
    : []),
  {
    component: (
      <>
        <img src={trashCan} alt="Remove" />
        {t('buttons.remove')}
      </>
    ),
    onClick: () => {
      handleRemoveProduct(selectedBProduct.uniqueId as string)
    },
  },
]
